import { useEffect, useRef } from "react";

/**
 * Resets pagination to page 1 whenever `value` (typically a debounced search
 * term) changes — but not on mount. A plain `useEffect(() => setPage(1),
 * [value, setPage])` also fires once on mount, which silently overwrites a
 * deep-linked `?page=2` back to 1 before the user ever sees it.
 *
 * Tracks the previous `value` (not a boolean "have I run" flag) because
 * React.StrictMode double-invokes mount effects in dev: a boolean flag is
 * already flipped by the first invocation, so the second one fires the reset
 * for real. Comparing against the last-seen value is idempotent regardless
 * of how many times the effect body runs for the same value.
 */
export function useResetPageOnChange(value: unknown, setPage: (page: number) => void): void {
  const previousValue = useRef(value);

  useEffect(() => {
    if (previousValue.current !== value) {
      previousValue.current = value;
      setPage(1);
    }
  }, [value, setPage]);
}
